import { useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, Snackbar, Tooltip,
  Typography,
} from '@mui/material'
import { CheckCircle, LockOutlined } from '@mui/icons-material'
import { billingApi, TenantBundle, TenantFeature } from '../services/api'
import { copyFor } from './bundleCopy'

/**
 * What this tenant has bought, and what the rest would do for them.
 *
 * Billing showed a plan, a price and quotas, and said nothing about bundles —
 * so a customer could not see on their own billing page which of Core,
 * Exposure and Evidence they held, let alone what the others were. The
 * provisioning UI knew, but that is platform-admin only.
 *
 * Per bundle, independently
 * -------------------------
 * Exposure and Evidence are sold separately and each gets its own state and
 * its own ask. A single "contact us" button would lose which one was clicked,
 * and sales would open the conversation not knowing what was wanted. The
 * request endpoint takes one bundle for the same reason.
 *
 * No prices
 * ---------
 * A bundle has no list figure: the agent band and the bundles resolve into a
 * contract together. So this says what a bundle DOES and offers to start the
 * conversation. Inventing a number here — even a "from" — would be a quote
 * nobody authorised.
 *
 * `partial` is shown, not hidden
 * -----------------------------
 * A bundle whose features resolve unevenly is a real state: an incomplete
 * grant, or a capability switched off platform-wide. It reads as "partially
 * active" with the working capabilities listed, because telling a paying
 * customer a bundle is active while part of it is dark is how the gap stays
 * invisible until they notice something missing.
 */
interface BundlesPanelProps {
  bundles: Record<string, TenantBundle> | undefined
  /** Resolved per-feature state, for listing what is inside each bundle in the
   *  customer's own vocabulary rather than by entitlement key. */
  features: TenantFeature[] | undefined
  tenantId: string
}

const BundlesPanel = ({ bundles, features, tenantId }: BundlesPanelProps) => {
  const [asking, setAsking] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [asked, setAsked] = useState<string[]>([])

  if (!bundles) return null

  const ask = async (name: string) => {
    setAsking(name)
    setError(null)
    try {
      const resp = await billingApi.requestBundle(tenantId, name)
      setNotice(resp.data?.message || 'Thanks — we will be in touch.')
      setAsked((prev) => [...prev, name])
    } catch (e: any) {
      setError(
        e?.response?.data?.detail
        || 'Could not send that just now. Please email sales@tryarias.com.',
      )
    } finally {
      setAsking(null)
    }
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Bundles
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What your contract includes. Exposure and Evidence are independent —
          either, both, or neither.
        </Typography>

        {Object.entries(bundles).map(([name, bundle], i) => {
          const members = (features || []).filter(
            (f) => f.bundle === name,
          )
          const active = members.filter((f) => f.enabled)
          const held = bundle.enabled
          const partial = bundle.partial
          const alreadyAsked = asked.includes(name)

          return (
            <Box key={name}>
              {i > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {bundle.label}
                    </Typography>
                    {held && (
                      <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircle sx={{ fontSize: 15 }} />}
                        label={bundle.grantable ? 'Added' : 'Included'}
                      />
                    )}
                    {!held && partial && (
                      <Tooltip title="Some capabilities in this bundle resolve and some do not — an incomplete grant, or one switched off in this environment. Worth asking us about.">
                        <Chip size="small" color="warning" variant="outlined" label="Partially active" />
                      </Tooltip>
                    )}
                    {!held && !partial && (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<LockOutlined sx={{ fontSize: 14 }} />}
                        label="Not added"
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {bundle.description}
                  </Typography>

                  {/* What is inside, in the customer's vocabulary. For a bundle
                      they do not hold this is the substance of the offer, so it
                      is listed either way rather than only when held. */}
                  <Box sx={{ pl: 0.5 }}>
                    {members.map((f) => {
                      const copy = copyFor(f.key)
                      return (
                        <Tooltip key={f.key} title={copy?.detail || f.description}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                            {f.enabled
                              ? <CheckCircle sx={{ fontSize: 13, color: 'success.main' }} />
                              : <LockOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />}
                            <Typography
                              variant="caption"
                              color={f.enabled ? 'text.primary' : 'text.secondary'}
                            >
                              {f.label}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )
                    })}
                    {/* Sold inside Evidence and gated through its own path, so
                        it is not in the feature list the API returns. Named
                        here or a customer reading this page would not know it
                        is part of what they are buying. */}
                    {name === 'evidence' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Also includes Compliance Posture and your own
                        enterprise frameworks.
                      </Typography>
                    )}
                  </Box>

                  {held && active.length < members.length && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.75 }}>
                      {members.length - active.length} of {members.length}{' '}
                      not currently resolving — contact us.
                    </Typography>
                  )}
                </Box>

                {/* One ask per bundle. Core has no switch, so it gets none. */}
                {bundle.grantable && !held && (
                  <Button
                    size="small"
                    variant={partial ? 'outlined' : 'contained'}
                    disabled={asking === name || alreadyAsked}
                    onClick={() => ask(name)}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {alreadyAsked ? 'Requested' : asking === name ? 'Sending…' : 'Add'}
                  </Button>
                )}
              </Box>
            </Box>
          )
        })}

        {/* No figure, and said plainly rather than left as an absence a reader
            has to notice. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Bundles are priced with your agent band as one contract, so there is
          no per-bundle list price. Adding one starts a conversation; nothing
          changes on your account until it is agreed.
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      </CardContent>

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        message={notice || ''}
      />
    </Card>
  )
}

export default BundlesPanel
